import React, { useEffect, useState, useMemo, useCallback } from "react";
// import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  // Button,
  CardContent,
  Grid,
  Typography,
  Skeleton,
  Stack,
  Alert,
} from "@mui/material";
import GlassCard from "../Cards/GlassCard";
// import {
//   BarChart,
//   Bar,
//   XAxis,
//   YAxis,
//   CartesianGrid,
//   Tooltip,
//   ResponsiveContainer,
// } from "recharts";
import CodeRoundedIcon from "@mui/icons-material/CodeRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
// import LocalOfferRoundedIcon from "@mui/icons-material/LocalOfferRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import { ctGetBatchInsights } from "../../services/api";
import {
  PRIMARY,
  SUCCESS,
  WARNING,
  INDIGO,
  DANGER,
  gradientTextSx,
  // dashboardTooltipSurfaceSx,
} from "../../utils/colors";

// const compareIsoWeekKeys = (a, b) => {
//   const [ya, wa] = a.split("-W").map(Number);
//   const [yb, wb] = b.split("-W").map(Number);
//   if (ya !== yb) return ya - yb;
//   return wa - wb;
// };

/** Last up to 6 weeks from sorted weeklyProgress (most recent first in slice). */
// const lastSixWeeksChartData = (weeklyProgress) => {
//   if (!weeklyProgress?.length) return [];
//   const sorted = [...weeklyProgress].sort((x, y) =>
//     compareIsoWeekKeys(x.week, y.week)
//   );
//   return sorted.slice(-6).map((row) => ({
//     ...row,
//     label: row.week?.replace(/^\d{4}-W/, "W") ?? row.week,
//   }));
// };

// const CustomTooltip = ({ active, payload, label }) => {
//   if (!active || !payload?.length) return null;
//   return (
//     <Box
//       sx={{
//         ...dashboardTooltipSurfaceSx,
//         borderRadius: 2,
//         p: 1.5,
//       }}
//     >
//       {label && (
//         <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
//           {label}
//         </Typography>
//       )}
//       {payload.map((entry, idx) => (
//         <Typography key={idx} variant="body2" fontWeight={600}>
//           Unique solves: {entry.value}
//         </Typography>
//       ))}
//     </Box>
//   );
// };

const SummaryCard = ({ icon, label, value, color }) => (
  <GlassCard sx={{ borderRadius: 4, height: "100%" }}>
    <CardContent
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        p: 2.5,
        "&:last-child": { pb: 2.5 },
      }}
    >
      <Box
        sx={{
          p: 1.5,
          borderRadius: 3,
          bgcolor: `${color}1a`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {React.cloneElement(icon, { sx: { color, fontSize: 28 } })}
      </Box>
      <Box>
        <Typography
          sx={{
            fontSize: "1.75rem",
            fontWeight: 700,
            letterSpacing: "-0.03em",
            lineHeight: 1.15,
            color: "text.primary",
          }}
        >
          {value}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          fontWeight={500}
          mt={0.75}
          sx={{ fontSize: "0.8125rem", lineHeight: 1.45 }}
        >
          {label}
        </Typography>
      </Box>
    </CardContent>
  </GlassCard>
);

// const ChartCard = ({ title, subtitle, children }) => (
//   <GlassCard sx={{ borderRadius: 4, height: "100%" }}>
//     <CardContent sx={{ p: 2.5, height: "100%", "&:last-child": { pb: 2.5 } }}>
//       <Box sx={{ mb: 2 }}>
//         <Typography variant="subtitle1" fontWeight={600} sx={{ letterSpacing: "-0.01em" }}>
//           {title}
//         </Typography>
//         {subtitle && (
//           <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75, lineHeight: 1.45 }}>
//             {subtitle}
//           </Typography>
//         )}
//       </Box>
//       {children}
//     </CardContent>
//   </GlassCard>
// );

const NEVER_SUBMITTED_DAYS = 999999;

const formatInactiveLabel = (daysInactive) => {
  if (daysInactive == null) return "Inactive";
  if (daysInactive >= NEVER_SUBMITTED_DAYS) return "Never submitted";
  return `Inactive for ${daysInactive} day${daysInactive === 1 ? "" : "s"}`;
};

const formatLastActiveShort = (lastActive) => {
  if (!lastActive || typeof lastActive !== "string") return "—";
  try {
    const d = new Date(lastActive);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
};

const isSevereAtRisk = (daysInactive) =>
  daysInactive > 10 || daysInactive >= NEVER_SUBMITTED_DAYS;

/**
 * Batch insights from GET /api/coding-tracker/batch-insights/:batchId
 */
const BatchInsightsCard = ({ batchId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchInsights = useCallback(async () => {
    if (!batchId) return;
    setLoading(true);
    setError(null);
    try {
      const { data: json } = await ctGetBatchInsights(batchId);
      setData(json);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to load batch insights.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [batchId]);

  useEffect(() => {
    fetchInsights();
  }, [fetchInsights]);

  // const chartData = useMemo(() => lastSixWeeksChartData(data?.weeklyProgress), [data]);

  // const topThree = useMemo(() => (data?.topTopics ?? []).slice(0, 3), [data]);

  const summaryCards = useMemo(() => {
    if (!data) return [];
    return [
      {
        icon: <CodeRoundedIcon />,
        label: "Avg Solved",
        value: data.avgSolved ?? 0,
        color: SUCCESS,
      },
      {
        icon: <PeopleAltRoundedIcon />,
        label: "Active Students",
        value: data.activeStudents ?? 0,
        color: PRIMARY,
      },
      {
        icon: <EmojiEventsRoundedIcon />,
        label: "Completion %",
        value: `${data.completionRate ?? 0}%`,
        color: WARNING,
      },
      {
        icon: <GroupsRoundedIcon />,
        label: "Total Students",
        value: data.totalStudents ?? 0,
        color: INDIGO,
      },
      {
        icon: <ErrorOutlineRoundedIcon />,
        label: "At Risk",
        value: data.atRiskStudents ?? 0,
        color: DANGER,
      },
    ];
  }, [data]);

  const showEmpty = !loading && !error && data && (data.totalStudents ?? 0) === 0;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" fontWeight={700} sx={{ ...gradientTextSx, mb: 2 }}>
        Batch insights
      </Typography>

      {error && (
        <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && (
        <Stack spacing={2}>
          <Grid container spacing={3.5}>
            {[0, 1, 2, 3, 4].map((i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <GlassCard sx={{ borderRadius: 4 }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Skeleton variant="rounded" width={56} height={56} sx={{ borderRadius: 3 }} />
                      <Box flex={1}>
                        <Skeleton width="60%" height={32} />
                        <Skeleton width="80%" height={20} sx={{ mt: 1 }} />
                      </Box>
                    </Stack>
                  </CardContent>
                </GlassCard>
              </Grid>
            ))}
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <GlassCard sx={{ borderRadius: 4 }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Skeleton width="40%" height={24} sx={{ mb: 2 }} />
                  <Skeleton variant="rounded" width="100%" height={160} />
                </CardContent>
              </GlassCard>
            </Grid>
            <Grid item xs={12} md={5}>
              <GlassCard sx={{ borderRadius: 4 }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Skeleton width="35%" height={24} sx={{ mb: 2 }} />
                  <Skeleton height={22} sx={{ mb: 1 }} />
                  <Skeleton height={22} sx={{ mb: 1 }} />
                  <Skeleton height={22} />
                </CardContent>
              </GlassCard>
            </Grid>
            <Grid item xs={12}>
              <GlassCard sx={{ borderRadius: 4 }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Skeleton width="28%" height={26} sx={{ mb: 2 }} />
                  <Skeleton height={56} sx={{ mb: 1.5, borderRadius: 2 }} />
                  <Skeleton height={56} sx={{ mb: 1.5, borderRadius: 2 }} />
                  <Skeleton height={56} sx={{ mb: 2, borderRadius: 2 }} />
                  <Skeleton width={220} height={36} sx={{ borderRadius: 1 }} />
                </CardContent>
              </GlassCard>
            </Grid>
          </Grid>
        </Stack>
      )}

      {!loading && showEmpty && (
        <GlassCard hoverable={false} sx={{ borderRadius: 4, py: 6, textAlign: "center" }}>
          <Typography color="text.secondary">No data yet for this batch</Typography>
        </GlassCard>
      )}

      {!loading && !showEmpty && data && (
        <>
          <Grid container spacing={3.5} sx={{ mb: 3 }}>
            {summaryCards.map((card) => (
              <Grid item xs={12} sm={6} md={4} key={card.label}>
                <SummaryCard {...card} />
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={3}>
            {/* <Grid item xs={12} md={7}>
              <ChartCard title="Batch assignments — weekly solves (last 6 weeks)">
                {chartData.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" py={4} textAlign="center">
                    No solves on batch-assigned problems in this window, or add problems to assignments first.
                  </Typography>
                ) : (
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart
                      data={chartData}
                      margin={{ top: 4, right: 8, left: 0, bottom: 4 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} />
                      <YAxis
                        allowDecimals={false}
                        width={44}
                        tick={{ fontSize: 11, fill: "#64748b" }}
                        tickFormatter={(v) => String(Math.round(Number(v)))}
                      />
                      <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
                      <Bar dataKey="solved" name="Unique solves" fill={PRIMARY} radius={[4, 4, 0, 0]} maxBarSize={48} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </Grid> */}
            {/* <Grid item xs={12} md={5}>
              <ChartCard
                title="Top topics"
                subtitle="Sum of tag hits: each distinct solved batch problem adds once per tag on that problem (so one problem can add several topics)."
              >
                {topThree.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" py={3}>
                    No topic tags on assigned problems yet.
                  </Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {topThree.map((row) => (
                      <Box
                        key={row.topic}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          py: 1,
                          px: 1.5,
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "divider",
                          bgcolor: (theme) =>
                            theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "grey.50",
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center">
                          <LocalOfferRoundedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                          <Typography variant="body2" fontWeight={600}>
                            {row.topic}
                          </Typography>
                        </Stack>
                        <Typography variant="body2" fontWeight={700} color="primary">
                          {row.count}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                )}
              </ChartCard>
            </Grid> */}
          </Grid>

          <GlassCard sx={{ borderRadius: 4, mt: 3 }}>
            <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                gap={1}
                mb={2}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  sx={{ letterSpacing: "-0.01em" }}
                >
                  At-Risk Students
                </Typography>
              </Stack>

              {(data.atRiskStudents ?? 0) > 0 && (
                <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                  At-risk threshold: last submission was <strong>more than{" "}
                  {data.atRiskInactiveThresholdDays ?? 5} full days</strong> ago.{" "}
                  {data.atRiskStudents} student{data.atRiskStudents !== 1 ? "s" : ""} meet that rule
                </Typography>
              )}

              {(data.atRiskStudents ?? 0) === 0 ? (
                <Typography variant="body2" color="text.secondary" py={2} textAlign="center">
                  No at-risk students 🎉
                </Typography>
              ) : (
                <>
                  <Box sx={{ maxHeight: 400, overflowY: "auto", pr: 0.5 }}>
                  <Stack spacing={1.25}>
                    {(data.atRiskList ?? []).map((row, idx) => {
                      const severe = isSevereAtRisk(row.daysInactive);
                      return (
                        <Box
                          key={String(row.studentId ?? idx)}
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1.5,
                            py: 1.25,
                            px: 1.5,
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: severe ? "error.main" : "divider",
                            borderLeftWidth: severe ? 4 : 1,
                            borderLeftColor: severe ? "error.main" : undefined,
                            bgcolor: (theme) =>
                              severe
                                ? theme.palette.mode === "dark"
                                  ? "rgba(224, 49, 49, 0.12)"
                                  : "rgba(224, 49, 49, 0.06)"
                                : theme.palette.mode === "dark"
                                  ? "rgba(255,255,255,0.03)"
                                  : "grey.50",
                          }}
                        >
                          <ErrorOutlineRoundedIcon
                            sx={{
                              fontSize: 22,
                              color: "error.main",
                              flexShrink: 0,
                              mt: 0.15,
                              opacity: severe ? 1 : 0.85,
                              filter: severe ? "drop-shadow(0 0 6px rgba(211,47,47,0.45))" : "none",
                            }}
                          />
                          <Box flex={1} minWidth={0}>
                            <Typography variant="body2" fontWeight={700} noWrap title={row.name}>
                              {row.name || "—"}
                            </Typography>
                            <Typography
                              variant="body2"
                              fontWeight={severe ? 700 : 600}
                              color={severe ? "error.main" : "text.primary"}
                              sx={{ mt: 0.25 }}
                            >
                              {formatInactiveLabel(row.daysInactive)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                              Last active: {formatLastActiveShort(row.lastActive)}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                  </Box>

                  {/* <Button
                    variant="outlined"
                    color="error"
                    component={RouterLink}
                    to={batchId ? `/batch/${batchId}/students` : "#"}
                    disabled={!batchId}
                    sx={{ mt: 2.5, fontWeight: 600 }}
                  >
                    View All At-Risk Students
                  </Button> */}
                </>
              )}
            </CardContent>
          </GlassCard>
        </>
      )}
    </Box>
  );
};

export default BatchInsightsCard;
